// libs
import React, { memo, useCallback, useEffect, useState } from 'react';
import { styled } from '@nykaa/ui-components';
import { connect } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import get from 'lodash/get';

// components
import IOElement from 'shared/components/IOElement';
import Virtualize from 'shared/components/Virtualize';
import SomethingWentWrong from 'shared/components/SomethingWentWrong';
import ReviewAllImages, { IMAGE_VIEW_TYPE } from 'shared/components/ReviewAllImages';
import Wrapper from '../components/Wrapper';
import Placeholder from '../components/Placeholder';
import NoReviewFound from '../components/NoReviewFound';
import ReviewRow from '../components/ReviewRow';
import ReviewSummary from '../components/ReviewSummary';

// store
import { fetchAllReviews } from 'shared/store/details/services';
import { clearReviews } from 'shared/store/details/actions';
import {
  getAllReviews,
  isReviewFetching,
  allReviewPageNumber,
  isReviewError,
} from 'shared/store/details/selectors';
import { setIsLikedByUser } from 'shared/store/details/actions';
import { isLoggedIn } from 'shared/store/app/selectors';

// api
import { getReviewSummary, likedOrDislikedReview } from 'shared/api/product-details/index';

// helper function
import { createReverseArray } from 'shared/layouts/ReviewList/helper';
import { log } from 'shared/helpers/newRelic';
import { TAG_IDENTIFIER } from 'shared/helpers/SentryLogger/constants';
import { onReviewPageLoad } from 'shared/layouts/ReviewList/analytics';

// constants
import { REVIEW_DETAILS_SOURCES } from 'shared/components/ReviewCard/constants';
import {
  getStoredReviewToBeLiked,
  removeReviewToBeLiked,
  updateReviewToBeLiked,
} from 'shared/components/ReviewCard/Helpful/helpers';
import { STATUS, HELPFUL_TEXTS } from 'shared/components/ReviewCard/Helpful/helpers';
import { TRACK_TYPE } from 'shared/helpers/analytics/constants';
import { PDP_EVENTS } from 'shared/helpers/analytics/constants/pdpEvents';

//analytics
import NFA from 'shared/helpers/analytics/NFAnalytics';

// defs
import {
  IReviewMarkedHelpfulApiResponse,
  IReviewMarkedHelpfulApiError,
} from 'shared/components/ReviewCard/Helpful/types';

interface IReviewListProps {
  reviewData: any;
  isFetching: boolean;
  isError: boolean;
  fetchReviews: (dispatch: any) => Promise<void>;
  clearReviewData: typeof clearReviews;
  currentPageNo: number;
  isLoggedInUser: boolean;
  parentRef?: React.RefObject<HTMLElement> | null;
  isMobile?: boolean;
}

const PAGE_SIZE = 10;
const ROOT_MARGIN_IO = { rootMargin: '80% 0px 0px 0px' };
const MAX_RATING_COUNT = 5;

const AllImagesContainer = styled.div`
  margin: ${({ theme }) => `0 ${theme.spacing.spacing100}`};
`;

interface IReviewSummary {
  aggregated: string;
  totalCount: number;
  ratingSummary: {
    count: string;
    star: number;
    per: number;
  }[];
  isFetching: boolean;
  isMobile?: boolean;
}

const initialReviewSummary = {
  aggregated: '',
  totalCount: 0,
  ratingSummary: [],
  isFetching: true,
};

function ReviewList({
  reviewData,
  isFetching,
  isError,
  fetchReviews,
  currentPageNo,
  clearReviewData,
  isLoggedInUser,
  parentRef = null,
  isMobile = true,
}: IReviewListProps) {
  const [reviewSummaryData, setReviewSummaryData] = useState<IReviewSummary>(initialReviewSummary);

  const { id: productId, slug } = useParams();
  const { search } = useLocation();

  useEffect(() => {
    NFA.track({
      event: PDP_EVENTS.REVIEW_PAGE_LOADED,
      type: TRACK_TYPE.EVENT,
    });
  }, []);

  const getReviews = useCallback(() => {
    const params = new URLSearchParams(search);
    const designCode = params.get('designCode');
    const id = designCode ? unescape(designCode) : unescape(productId);
    fetchReviews({
      productId: id,
      size: PAGE_SIZE,
      page: currentPageNo + 1,
    });
  }, [currentPageNo, fetchReviews, productId, search]);

  // fetch review summary data using getReviewSummary and set them on reviewSummaryData state
  useEffect(() => {
    let isValid = true;
    getReviewSummary(productId)
      .then(({ response }) => {
        if (isValid === false) {
          return;
        }
        const reviewSplitup = get(response, 'product.review_rating_json.review_splitup', {});

        // creating a descending order array with elements of type IRatingSummary
        const tempRatingSummary = createReverseArray(reviewSplitup, MAX_RATING_COUNT);
        setReviewSummaryData({
          ratingSummary: [...tempRatingSummary],
          aggregated: reviewSplitup.aggregated,
          totalCount: reviewSplitup.total_count,
          isFetching: false,
        });
        const reviewRatingData = get(response, 'product.review_rating_json', {});
        onReviewPageLoad({ productId, reviewRatingData, reviewData });
      })
      .catch((error) => {
        log({
          errTitle: 'Error in getting review summary',
          error,
          tags: { [TAG_IDENTIFIER.MODULE]: 'pdp all review summary' },
        });
        setReviewSummaryData({
          ...initialReviewSummary,
          isFetching: false,
        });
      });

    return () => {
      isValid = false;
    };
  }, [productId, reviewData]);

  // call like api for review_to_be_liked if review is saved in session storage and user has logged in
  useEffect(() => {
    if (!isLoggedInUser) {
      return;
    }
    // get review_to_be_liked from session storage
    const reviewToBeLiked = getStoredReviewToBeLiked();

    // if review_to_be_liked is not empty in session storage
    if (Object.keys(reviewToBeLiked).length === 0) {
      return;
    }

    // if api has been called for review_to_be_liked, remove the review_to_be_liked from session storage
    if (reviewToBeLiked.apiCalledStatus === true) {
      removeReviewToBeLiked();
      return;
    }

    // update session storage, api is called for review_to_be_liked
    updateReviewToBeLiked(STATUS.CALLED);

    // calling api for review_to_be_liked
    likedOrDislikedReview(productId, reviewToBeLiked.id, HELPFUL_TEXTS.ADD)
      .then((res: IReviewMarkedHelpfulApiResponse) => {
        if (res.error === false) {
          return;
        }
        setIsLikedByUser(reviewToBeLiked.id, false);
      })
      .catch((err: IReviewMarkedHelpfulApiError) => {
        updateReviewToBeLiked(STATUS.FAILED);
        setIsLikedByUser(reviewToBeLiked.id, false);

        log({
          errTitle: 'Error in likedOrDislikedReview api',
          message: HELPFUL_TEXTS.HELPFUL_FAILED_MESSAGE,
          error: err,
          tags: { [TAG_IDENTIFIER.MODULE]: HELPFUL_TEXTS.HELPFUL_REVIEW },
        });
      });
  }, [isLoggedInUser, productId]);

  useEffect(() => {
    return () => {
      clearReviewData();
    };
  }, [clearReviewData, productId, slug]);

  if (isError) {
    return (
      <Wrapper>
        <SomethingWentWrong />
      </Wrapper>
    );
  }

  if (reviewData.length == 0 && isFetching === false) {
    return (
      <Wrapper>
        <NoReviewFound />
      </Wrapper>
    );
  }

  return (
    <Wrapper isMobile={isMobile}>
      <ReviewSummary
        aggregated={reviewSummaryData.aggregated}
        ratingSummary={reviewSummaryData.ratingSummary}
        totalCount={reviewSummaryData.totalCount}
        isFetching={reviewSummaryData.isFetching}
        isMobile={isMobile}
      />
      <AllImagesContainer>
        <ReviewAllImages
          viewType={IMAGE_VIEW_TYPE.RAIL}
          source={REVIEW_DETAILS_SOURCES.IMAGE_RAIL_ALL_REVIEW_SECTION}
          isMobile={isMobile}
        />
      </AllImagesContainer>

      <Virtualize parentRef={parentRef}>
        {reviewData.map((singleReview: Review.IReviewData, indx: number) => {
          return (
            <ReviewRow
              review={singleReview}
              key={singleReview.childId + '_' + indx}
              index={indx}
              productId={productId}
              isMobile={isMobile}
            />
          );
        })}
      </Virtualize>

      {reviewData.length === currentPageNo * PAGE_SIZE && (
        <>
          <IOElement onVisible={getReviews} options={ROOT_MARGIN_IO} />
          <Placeholder />
          <Placeholder />
          <Placeholder />
        </>
      )}
    </Wrapper>
  );
}

const mapStateToProps = (state: any) => {
  return {
    reviewData: getAllReviews(state),
    isFetching: isReviewFetching(state),
    isError: isReviewError(state),
    currentPageNo: allReviewPageNumber(state),
    isLoggedInUser: isLoggedIn(state),
  };
};

const mapDispatchToProp = {
  fetchReviews: fetchAllReviews,
  clearReviewData: clearReviews,
  setIsLikedByUser: setIsLikedByUser,
};

export default connect(mapStateToProps, mapDispatchToProp)(memo(ReviewList));
